import { ActionDropDown, Dropdown, useNotifications } from '@doveit/bricks';
import React from 'react';
import { TaskStatus } from '../../../domain/types';
import { taskStatusLabels } from '../../../labels';
import { Task } from '../../../providers/api/dtos';
import TaskStatusBadge from '../../components/task-status-badge/TaskStatusBadge';
import {
  taskStatusColors,
  taskStatusIcons,
} from '../../constants';
import { useComputedTaskStatus } from '../../hooks/use-computed-task-status/useComputedTaskStatus';
import { updateTask } from '../../../providers/api/task/taskProvider';

export interface UpdateTaskStatusProps {
  task: Task,
  showAsBadge?: boolean,
  emphasis?: 'low' | 'high',
  size?: 'XS' | 'S',
  showIcon?: boolean,
  onSuccess?: (updatedTask: Task) => void,
}

export const UPDATE_TASK_STATUS_SUCCESS_MESSAGE = 'Lo stato dell\'attività è stato modificato con successo.';
export const UPDATE_TASK_STATUS_ERROR_MESSAGE = 'Non è stato possibile modificare lo stato dell\'attività.';

const UpdateTaskStatus: React.FC<UpdateTaskStatusProps> = ({
  task,
  showAsBadge = false,
  emphasis = 'low',
  size = 'S',
  showIcon,
  onSuccess,
}) => {
  const { status, canBeUpdated, canBeUpdatedTo } = useComputedTaskStatus(task);
  const [isSaving, setIsSaving] = React.useState(false);
  const { addSuccess, addError } = useNotifications();

  const handleUpdate = React.useCallback((newStatus: TaskStatus) => async () => {
    try {
      setIsSaving(true);

      const updatedTask = await updateTask(task.id!, {
        ...task,
        status: newStatus,
      });

      setIsSaving(false);
      addSuccess(UPDATE_TASK_STATUS_SUCCESS_MESSAGE);
      onSuccess?.(updatedTask);
    } catch (_) {
      setIsSaving(false);
      addError(UPDATE_TASK_STATUS_ERROR_MESSAGE);
    }
  }, [addError, addSuccess, onSuccess, task]);

  if (!canBeUpdated || showAsBadge) {
    return (
      <TaskStatusBadge
        status={task.status}
        size={size}
        emphasis={emphasis}
        showIcon={showIcon}
      />
    );
  }

  return (
    <ActionDropDown
      aria-label="Seleziona un nuovo stato dell'attività"
      label={taskStatusLabels[status]}
      color={taskStatusColors[status]}
      iconLeft={showIcon ? { path: taskStatusIcons[status] } : undefined}
      emphasis={emphasis}
      size={size}
    >
      {Object.values(TaskStatus).map((s) => canBeUpdatedTo(s) && (
        <Dropdown.Option
          key={s}
          label={taskStatusLabels[s]}
          aria-label={`Modifica lo stato dell'attività in ${taskStatusLabels[s]}`}
          onClick={handleUpdate(s)}
          loading={isSaving}
        />
      ))}
    </ActionDropDown>
  );
};

export default UpdateTaskStatus;
